import React, { useEffect } from "react";
import { useFormContext, RegisterOptions } from "react-hook-form";
import { MenuItem, Grid, Typography } from "@mui/material";
import { RHFSelectField } from "../../../atoms/form-fields/RHFSelectField";
import { RHFSwitchField } from "../../../atoms/form-fields/RHFSwitchField";
import { RHFTextField } from "../../../atoms/form-fields/RHFTextField";
import { IndexedCardInfo } from "../../../atoms/indexed-card-info";
import {
  isValidUKCompanyRegNumber,
  isValidVATNumber,
} from "../helpers/validators";
import { DatePicker } from "../../../DatePicker";
import { isValidPostcode } from "../../AddressPicker/helpers/isValidPostcode";
import { isOver18YearsOld } from "../../../shared/date";
import { StatusChip } from "../../../StatusChip/StatusChip";

interface AccountDetailsProps {
  cardIndex: string;
  isVfDirect?: boolean;
}

export const AccountDetails = ({
  cardIndex,
  isVfDirect = false,
}: AccountDetailsProps) => {
  const { watch, resetField } = useFormContext();
  const business_entity_type = watch("business_entity_type");
  const is_company_younger_than = watch("is_company_younger_than");
  const director_address_more_than_3_years_old = watch(
    "director_address_more_than_3_years_old"
  );
  const showDirectorDetails =
    business_entity_type === "Limited Company (LTD)" && is_company_younger_than;

  const nameValidationRules: RegisterOptions = {
    required: "Company Name is required",
    maxLength: {
      value: 40,
      message: "Must be 40 characters or less",
    },
  };

  if (isVfDirect) {
    nameValidationRules.pattern = {
      value: /^[a-zA-Z0-9,.:"()\s\\-]+$/i,
      message:
        'Company Name contains invalid characters. Only letters, numbers, spaces, and .,:"()- are allowed.',
    };
  }

  const companyTypes = isVfDirect
    ? [
        { value: "Limited Company (LTD)", label: "Limited Company (LTD)" },
        {
          value: "Sole Trader / Owner Manager",
          label: "Sole Trader / Owner Manager",
        },
        {
          value: "Limited Liability Partnership",
          label: "Limited Liability Partnership",
        },
        { value: "Charity", label: "Charity" },
      ]
    : [
        { value: "Limited Company (LTD)", label: "Limited Company (LTD)" },
        {
          value: "Sole Trader / Owner Manager",
          label: "Non-limited - Business owner",
        },
        { value: "Non-limited - Other", label: "Non-limited - Other" },
        { value: "Individual", label: "Individual" },
      ];

  const companySubtypes = [
    "Building and Friendly Societies",
    "Charities",
    "Community Interest Company",
    "Government Agencies",
    "Housing Associations",
    "Mutuals",
    "NHS Trusts",
    "Public Sector",
    "Religious Bodies/Churches etc",
    "Royal Charters",
    "Schools",
    "Sports clubs",
    "Youth Clubs/Community Centres",
  ];

  useEffect(() => {
    // Ensure these fields are cleared after updating the director_address_more_than_3_years_old select which
    // shows/hides these fields.
    resetField("director_previous_street");
    resetField("director_previous_city");
    resetField("director_previous_postcode");
  }, [director_address_more_than_3_years_old, resetField]);

  return (
    <IndexedCardInfo index={cardIndex} title="Account Details">
      <Grid container spacing={2}>
        <Grid item sm={6}>
          <RHFTextField
            name="name"
            label="Company Name"
            rules={nameValidationRules}
          />
        </Grid>
        <Grid item sm={6}>
          <RHFSelectField
            name="business_entity_type"
            label="Company Type"
            rules={{ required: true }}
          >
            {companyTypes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
            <MenuItem value="Professional Partnership">
              Professional Partnership
            </MenuItem>
            <MenuItem value="Business Partnership">
              Business Partnership
            </MenuItem>
            <MenuItem value="Public Limited Company (PLC)">
              Public Limited Company (PLC)
            </MenuItem>
            <MenuItem value="Government">
              Government (Central, Local or NHS Trust)
            </MenuItem>
            <MenuItem value="Individual Business User">
              Individual Business User
            </MenuItem>
            <MenuItem value="International Business User">
              International Business User
            </MenuItem>
          </RHFSelectField>
        </Grid>
        {business_entity_type === "Charity" && (
          <Grid item sm={6}>
            <RHFTextField label="Charity Number" name="charity_number" />
          </Grid>
        )}
        {business_entity_type === "Non-limited - Other" && (
          <Grid item sm={6}>
            <RHFSelectField
              name="organisation_type"
              label="Organisation Type"
              rules={{ required: true }}
            >
              {companySubtypes.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </RHFSelectField>
          </Grid>
        )}
        <Grid item sm={6}>
          <RHFTextField
            label="Company Registration Number"
            name="registered_number"
            rules={{
              required:
                business_entity_type === "Limited Company (LTD)" ||
                business_entity_type === "Public Limited Company (PLC)",
              validate: (val) =>
                business_entity_type === "Limited Company (LTD)" ||
                business_entity_type === "Public Limited Company (PLC)"
                  ? isValidUKCompanyRegNumber(val)
                  : val
                  ? isValidUKCompanyRegNumber(val)
                  : true,
            }}
          />
        </Grid>
        {!isVfDirect && business_entity_type === "Limited Company (LTD)" && (
          <Grid item sm={6}>
            <RHFSwitchField
              label="Is company less than 2 years old?"
              name="is_company_younger_than"
            />
          </Grid>
        )}
        <Grid item sm={6}>
          <RHFSelectField label="No. of employees" name="employees">
            <MenuItem value="0">Not Provided</MenuItem>
            <MenuItem value="1">1-10</MenuItem>
            <MenuItem value="11">11-20</MenuItem>
            <MenuItem value="21">21-50</MenuItem>
            <MenuItem value="51">51-100</MenuItem>
            <MenuItem value="101">101-499</MenuItem>
            <MenuItem value="500">500-999</MenuItem>
            <MenuItem value="1000">1000+</MenuItem>
          </RHFSelectField>
        </Grid>
        {business_entity_type !== "Charity" && (
          <Grid item sm={6}>
            <RHFTextField
              label="VAT Number"
              name="vat_number"
              rules={{
                validate: (val) => (val ? isValidVATNumber(val) : true),
              }}
            />
          </Grid>
        )}
        {!isVfDirect && showDirectorDetails && (
          <>
            <Grid item sm={12}>
              <Typography variant="h4">Director's Details</Typography>
            </Grid>
            <Grid item sm={6}>
              <RHFTextField
                label="First Name"
                name="director_first_name"
                rules={{ required: true }}
              />
            </Grid>
            <Grid item sm={6}>
              <RHFTextField
                label="Last Name"
                name="director_last_name"
                rules={{ required: true }}
              />
            </Grid>
            <Grid item sm={6}>
              <DatePicker
                name="director_birthdate"
                label={"Date Of Birth"}
                disableFuture
                margin="none"
                rules={{
                  required: true,
                  validate: (val: Date) => {
                    if (!isOver18YearsOld(val)) {
                      return "Must be over 18 years of age";
                    }
                    if (val.getFullYear() < 1900) {
                      return "Minimum date must be greater than 01/01/1900";
                    }
                    return true;
                  },
                }}
              />
            </Grid>
            <Grid item sm={12}>
              <Typography variant="h4">Director's Home Address</Typography>
            </Grid>
            <Grid item sm={6}>
              <RHFTextField
                label="Street"
                name="director_home_street"
                rules={{ required: true }}
              />
            </Grid>
            <Grid item sm={6}>
              <RHFTextField
                label="City"
                name="director_home_city"
                rules={{ required: true }}
              />
            </Grid>
            <Grid item sm={6}>
              <RHFTextField
                label="Postcode"
                name="director_home_postcode"
                rules={{
                  required: true,
                  validate: (value) =>
                    isValidPostcode(value) ? true : "Must be a valid postcode",
                }}
              />
            </Grid>
            <Grid item sm={6}>
              <RHFSelectField
                label="Have been at address for over 3 years?"
                name="director_address_more_than_3_years_old"
                rules={{ required: true }}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </RHFSelectField>
            </Grid>
            {director_address_more_than_3_years_old === "No" && (
              <>
                <Grid item sm={12}>
                  <Typography variant="h4">
                    Director's Previous Address
                  </Typography>
                </Grid>
                <Grid item sm={6}>
                  <RHFTextField
                    label="Street"
                    name="director_previous_street"
                    rules={{ required: true }}
                  />
                </Grid>
                <Grid item sm={6}>
                  <RHFTextField
                    label="City"
                    name="director_previous_city"
                    rules={{ required: true }}
                  />
                </Grid>
                <Grid item sm={6}>
                  <RHFTextField
                    label="Postcode"
                    name="director_previous_postcode"
                    rules={{
                      required: true,
                      validate: (value) =>
                        isValidPostcode(value)
                          ? true
                          : "Must be a valid postcode",
                    }}
                  />
                </Grid>
              </>
            )}
          </>
        )}
        <Grid item sm={12}>
          {!isVfDirect &&
            business_entity_type === "Sole Trader / Owner Manager" && (
              <StatusChip
                type="info"
                message="The Non-Limited - Business Owner option applies to SoHo‑type businesses, such as plumbers or
                joiners, that are not limited companies."
              />
            )}
          {!isVfDirect && business_entity_type === "Non-limited - Other" && (
            <StatusChip
              type="info"
              message="The Non-Limited - Other option covers organisations operating in the public or not‑for‑profit
              sector, including public sector bodies, dentists, charities, and similar entities."
            />
          )}
        </Grid>
      </Grid>
    </IndexedCardInfo>
  );
};
